<template>
    <flex-container>
        <text-decorator bold :size="FontSize.xs">
            Vestigingen
        </text-decorator>
        <flex-container :gap="Size.sm">
            <back-button :to="{ name: 'team-list' }" />
            <text-heading>
                Vestiging aanmaken
            </text-heading>
        </flex-container>
        <flex-container
            :screen-sizes="{
                desktop: 5,
            }"
        >
            <card-normal>
                <flex-container :gap="Size.lg">
                    <text-description v-if="hasWinckBranding()">
                        Voor het toevoegen van een nieuwe vestiging aan Winck, check eerst of een aparte huisstijl nodig is en maak deze aan.
                        Vul dan alle velden in; binnen een minuut is de vestiging toegevoegd.
                    </text-description>

                    <TeamForm ref="teamForm" />

                    <flex-container justify-content="flex-end" align-items="center">
                        <button-text @click="$router.push({ name: 'team-list' })">
                            Annuleren
                        </button-text>
                        <button-block
                            :disabled="!isValid"
                            @click="save"
                        >
                            Aanmaken
                        </button-block>
                    </flex-container>
                </flex-container>
            </card-normal>
        </flex-container>
    </flex-container>
</template>

<script setup lang="ts">
    import BackButton from '@/shared/components/BackButton.vue';
    import TeamForm, { type ExposedTeamForm } from '@/company/team/components/TeamForm.vue';
    import { computed, ref } from 'vue';
    import { useRouter } from 'vue-router';
    import { Branch, BranchAPI, BranchType, RealworksCredentials, RealworksCredentialsAPI } from '@keyvue/one-api-client-legacy';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import { v4 as uuid } from 'uuid';
    import {
        ButtonBlock,
        ButtonText,
        CardNormal,
        FlexContainer,
        FontSize,
        NotificationBlock,
        Size,
        TextDecorator,
        TextDescription,
        TextHeading,
        useInteractionBlocker,
        useNotify,
    } from '@keyvue/one-ui';
    import useEventBus from '@/services/eventbus/composables/useEventBus';
    import TeamCreatedEvent, { type TeamCreatedEventParameters } from '@/company/team/events/TeamCreatedEvent';
    import { ChangeTeamBrandCommand, ChangeTeamSocialMediaCommand, CreateCredentialsCommand, CredentialsId, TeamId } from '@keyvue/one-api-client';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';

    const { currentLegacyCompany, currentLegacyUser, client } = useClient();
    const { notify } = useNotify();
    const { blockInteractionForLoad } = useInteractionBlocker();
    const eventBus = useEventBus();
    const router = useRouter();
    const { hasWinckBranding } = useAppBranding();

    const newId = uuid();
    const teamForm = ref<ExposedTeamForm>();
    const validation = computed(() => teamForm.value?.formValidation.formValidationBindings);
    const isValid = computed<boolean>(() => validation.value?.isValid.value ?? false);

    function createRealworksCredential(
        type: 'taken' | 'agenda' | 'wonen' | 'relaties',
        token: string,
        branch: Branch,
    ): RealworksCredentials {
        return new RealworksCredentials(
            currentLegacyCompany.value,
            uuid(),
            true,
            new Date(),
            currentLegacyUser.value,
            new Date(),
            currentLegacyUser.value,
            token,
            null,
            false,
            type === 'wonen',
            type === 'agenda',
            type === 'taken',
            type === 'relaties',
            branch,
        );
    }

    async function save() {
        const clearBlock = blockInteractionForLoad('Bezig met het aanmaken van de vestiging');

        const formValues = teamForm.value?.form;

        if (!formValues) {
            clearBlock();

            notify({
                component: NotificationBlock,
                props: {
                    title: 'We konden de vestiging niet opslaan',
                    text: 'Probeer het later nog een keer.',
                    color: 'red-500',
                },
                duration: 4000,
            });

            return;
        }

        const branch = new Branch(
            currentLegacyCompany.value,
            [],
            newId,
            formValues.name,
            formValues.name,
            BranchType.WINCK,
            formValues.street,
            formValues.houseNumber,
            '',
            formValues.zipcode,
            formValues.city,
            'Nederland',
            formValues.phoneNumber,
            formValues.email,
            formValues.website,
            null,
            null,
            {},
            {},
            [],
            [],
            new Date(),
            new Date(),
        );

        await BranchAPI.create(branch);

        await new Promise((resolve) => { setTimeout(resolve, 2000); });

        if (hasWinckBranding()) {
            await client.dispatch(new ChangeTeamBrandCommand(
                new TeamId(newId),
                formValues.brand?.id ?? null,
            ));
        }

        await client.dispatch(new ChangeTeamSocialMediaCommand(
            new TeamId(newId),
            formValues.socialMedia.facebookUrl,
            formValues.socialMedia.instagramUrl,
            formValues.socialMedia.linkedinUrl,
            formValues.socialMedia.youtubeUrl,
            formValues.socialMedia.xUrl,
            formValues.socialMedia.pinterestUrl,
            formValues.socialMedia.tiktokUrl,
            formValues.socialMedia.vimeoUrl,
        ));

        await client.dispatch(new CreateCredentialsCommand(
            new CredentialsId(uuid()),
            formValues.emailCredentials.imapHostName,
            formValues.emailCredentials.imapPort,
            formValues.emailCredentials.smtpHostName,
            formValues.emailCredentials.smtpPort,
            formValues.emailCredentials.username,
            formValues.emailCredentials.password,
            new TeamId(newId),
        ));

        if (formValues.realworks.relatieToken) {
            await RealworksCredentialsAPI.create(createRealworksCredential('relaties', formValues.realworks.relatieToken, branch));
        }

        if (formValues.realworks.agendaToken) {
            await RealworksCredentialsAPI.create(createRealworksCredential('agenda', formValues.realworks.agendaToken, branch));
        }

        if (formValues.realworks.takenToken) {
            await RealworksCredentialsAPI.create(createRealworksCredential('taken', formValues.realworks.takenToken, branch));
        }

        if (formValues.realworks.wonenToken) {
            await RealworksCredentialsAPI.create(createRealworksCredential('wonen', formValues.realworks.wonenToken, branch));
        }

        clearBlock();

        notify({
            component: NotificationBlock,
            props: {
                title: 'Gelukt!',
                text: 'We hebben de nieuwe vestiging opgeslagen.',
                color: 'green-500',
            },
            duration: 4000,
        });

        eventBus.dispatch<TeamCreatedEventParameters>(
            TeamCreatedEvent,
            { teamId: newId },
        );

        await router.push({ name: 'team-list' });
    }
</script>
